import React from 'react';

/**
 * Global Error Boundary for Production
 * 
 * Catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI instead of the
 * component tree that crashed.
 * 
 * OWASP: Prevents Information Disclosure by hiding stack traces
 * from end users in production.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to console in development only
    if (process.env.NODE_ENV === 'development') {
      console.error('ErrorBoundary caught an error:', error, errorInfo);
    }
    
    // In production, you could send this to an error reporting service
    // Examples: Sentry, LogRocket, Bugsnag
    // if (process.env.NODE_ENV === 'production') {
    //   Sentry.captureException(error, { extra: errorInfo });
    // }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/app/dashboard';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
          <div className="text-center p-8 max-w-md">
            {/* Error Icon */}
            <div className="mx-auto w-16 h-16 mb-6 rounded-full bg-red-100 flex items-center justify-center">
              <svg 
                className="w-8 h-8 text-red-500" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" 
                />
              </svg>
            </div>

            {/* Error Message */}
            <h1 className="text-2xl font-bold text-gray-800 mb-3">
              Ha ocurrido un error inesperado
            </h1>
            <p className="text-gray-600 mb-8 leading-relaxed">
              Lo sentimos, algo salió mal. Por favor, intenta recargar la página 
              o vuelve al inicio. Si el problema persiste, contacta a soporte.
            </p>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={this.handleReload}
                className="px-6 py-2.5 bg-teal-600 text-white rounded-lg font-medium 
                         hover:bg-teal-700 transition-colors shadow-md hover:shadow-lg"
              >
                Recargar página
              </button>
              <button
                onClick={this.handleGoHome}
                className="px-6 py-2.5 bg-white text-gray-700 rounded-lg font-medium 
                         border border-gray-300 hover:bg-gray-50 transition-colors"
              >
                Ir al inicio
              </button>
            </div>

            {/* Support Info */}
            <p className="mt-8 text-sm text-gray-400">
              Código de referencia: ERR-{Date.now().toString(36).toUpperCase()}
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
